import { AfterViewInit, Component, ElementRef, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { DxChatComponent } from 'devextreme-angular/ui/chat';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { MessageEnteredEvent } from 'devextreme/ui/chat';
import ScrollView from 'devextreme/ui/scroll_view';
import { loadMessages } from 'devextreme/localization';
import { ThomasPrompt } from '../../dtos/training-assistant-chat';
import { ThomasAppConfig } from './thomas-app.config';
import { CURRENT_USER, ThomasConversationService } from './thomas-conversation.service';
import { ThomasPromptsComponent } from './thomas-prompts.component';
import { ThomasReplyComponent } from './thomas-reply.component';

loadMessages({ en: { 'dxChat-textareaPlaceholder': 'Ask Thomas anything...' } });

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- shared Thomas markup uses the js- prefix in every app
  selector: 'js-thomas-chat',
  templateUrl: './thomas-chat.component.html',
  imports: [DxChatComponent, DxTemplateDirective, ThomasReplyComponent, ThomasPromptsComponent]
})
export class ThomasChatComponent implements AfterViewInit, OnDestroy {

  readonly currentUser = CURRENT_USER;
  myPrompts: ThomasPrompt[] = [];
  companyPrompts: ThomasPrompt[] = [];
  // the welcome screen shows the user's own prompts unless they switch to the company's
  showCompanyPrompts = false;
  promptDraft: string | null = null;
  promptResponseId: string | null = null;

  private subscription = new Subscription();
  private promptsRequest: Subscription | null = null;
  private scrollFrame = 0;
  private scrollView: ScrollView | null = null;
  private atBottom = true;
  private readonly onScroll = () => {
    if (this.scrollView) {
      this.atBottom = this.scrollView.scrollHeight() - this.scrollView.scrollTop() - this.scrollView.clientHeight() < 80;
    }
  };

  thomas = inject(ThomasConversationService);
  private app = inject(ThomasAppConfig);
  private element = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    this.subscription.add(this.thomas.textUpdated.subscribe(() => this.followStream()));
    this.subscription.add(this.thomas.conversationReset.subscribe(() => this.atBottom = true));
    this.subscription.add(this.app.contextChanged.subscribe(() => {
      this.thomas.managingPrompts = false;
      this.myPrompts = [];
      this.companyPrompts = [];
      this.loadPrompts();
    }));
    this.loadPrompts();
  }

  ngAfterViewInit() {
    if (this.thomas.open) {
      this.reveal();
    }
  }

  onMessageEntered(e: MessageEnteredEvent) {
    this.thomas.send(e.message.text ?? '');
  }

  get prompts(): ThomasPrompt[] {
    return this.showCompanyPrompts ? this.companyPrompts : this.myPrompts;
  }

  showPromptSet(company: boolean) {
    this.showCompanyPrompts = company;
  }

  // null opens the list; a string opens a new prompt with that text
  managePrompts(draft: string | null = null, responseId: string | null = null) {
    this.promptDraft = draft;
    this.promptResponseId = responseId;
    this.thomas.managingPrompts = true;
  }

  closePrompts() {
    this.thomas.managingPrompts = false;
    this.focusComposer();
  }

  sendPrompt(text: string) {
    this.thomas.managingPrompts = false;
    this.thomas.send(text);
    this.focusComposer();
  }

  // the buttons are a shortcut; Thomas works the same when they cannot load
  loadPrompts() {
    this.promptsRequest?.unsubscribe();
    if (!this.thomas.available) {
      return;
    }
    this.promptsRequest = this.app.transport.getPrompts().subscribe({
      next: prompts => {
        this.myPrompts = prompts.filter(prompt => !prompt.isCompany);
        this.companyPrompts = prompts.filter(prompt => prompt.isCompany);
        // the switch is hidden without company prompts, so it must not be left on them
        if (!this.companyPrompts.length) {
          this.showCompanyPrompts = false;
        }
      },
      error: () => {
        this.myPrompts = [];
        this.companyPrompts = [];
      }
    });
  }

  reveal() {
    this.resolveScrollView();
    if (this.atBottom) {
      this.followStream();
    }
    this.focusComposer();
  }

  focusComposer() {
    this.element.nativeElement.querySelector<HTMLElement>('.dx-chat-textarea .dx-texteditor-input')?.focus({ preventScroll: true });
  }

  // dxChat 25.2 has no public scroll API. Measure user intent on scroll, then
  // follow after Angular renders the push, including a large final reply/card.
  private followStream() {
    this.resolveScrollView();
    if (!this.thomas.open || !this.atBottom || this.scrollFrame) {
      return;
    }
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      if (this.atBottom && this.scrollView) {
        this.scrollView.scrollTo({ top: this.scrollView.scrollHeight() });
      }
    });
  }

  private resolveScrollView() {
    const element = this.element.nativeElement.querySelector<HTMLElement>('.dx-chat-messagelist .dx-scrollable');
    const scrollView = element ? ScrollView.getInstance(element) as ScrollView : null;
    if (scrollView !== this.scrollView) {
      this.scrollView?.off('scroll', this.onScroll);
      this.scrollView = scrollView;
      this.scrollView?.on('scroll', this.onScroll);
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.promptsRequest?.unsubscribe();
    this.scrollView?.off('scroll', this.onScroll);
    this.scrollView = null;
    cancelAnimationFrame(this.scrollFrame);
  }
}
